import React from 'react';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, AreaChart, Area,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';

// Status → brand color, matching the Chargebacks screen tints.
const STATUS_COLORS = {
  APPROVED: '#16a34a', POSTED: '#2563eb', REJECTED: '#dc2626', IN_ERROR: '#f97316',
  VALIDATED: '#0d9488', IMPORTED: '#6b7280', SUPERSEDED: '#7c3aed', CANCELLED: '#9ca3af',
  PENDING: '#eab308'
};
const PALETTE = ['#2563eb', '#16a34a', '#f97316', '#7c3aed', '#0d9488', '#dc2626', '#eab308', '#db2777', '#0891b2', '#65a30d'];

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const money = (v) => '$' + num(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
const moneyShort = (v) => {
  const n = num(v);
  if (Math.abs(n) >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M';
  if (Math.abs(n) >= 1e3) return '$' + (n / 1e3).toFixed(1) + 'K';
  return '$' + n.toFixed(0);
};
const statusLabel = (s) => (s === 'IN_ERROR' ? 'In Error' : String(s || '').charAt(0) + String(s || '').slice(1).toLowerCase());

const Kpi = ({ label, value, tone = 'blue' }) => {
  const tones = {
    blue: 'bg-blue-50 text-blue-700', green: 'bg-green-50 text-green-700',
    purple: 'bg-purple-50 text-purple-700', orange: 'bg-orange-50 text-orange-700',
    teal: 'bg-teal-50 text-teal-700', gray: 'bg-gray-50 text-gray-700'
  };
  return (
    <div className={`rounded-xl p-4 ${tones[tone] || tones.blue}`}>
      <p className="text-xs font-medium uppercase tracking-wide opacity-80">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </div>
  );
};

const ChartCard = ({ title, subtitle, height = 300, children }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <div className="mb-3">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </div>
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  </div>
);

const Empty = ({ label = 'No data for this selection.' }) => (
  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-10 text-center text-sm text-gray-500">{label}</div>
);

const axis = { fontSize: 12, stroke: '#94a3b8' };
const tooltipStyle = { borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 };

// ---- Chargeback Summary report ------------------------------------------
function ChargebackCharts({ data }) {
  const s = data.summary || {};
  const statusData = (data.statusBreakdown || []).map((x) => ({
    name: statusLabel(x.status), status: x.status, count: num(x.count), amount: num(x.totalAmount)
  }));
  const trend = (data.monthlyTrends || []).map((x) => ({ period: x.period, count: num(x.count), amount: num(x.totalAmount) }));
  const custs = (data.topCustomers || []).slice(0, 10).map((x) => ({ name: x.customer?.name || 'Unknown', amount: num(x.totalAmount), count: num(x.count) }));
  const prods = (data.topProducts || []).slice(0, 10).map((x) => ({ name: x.product?.name || 'Unknown', amount: num(x.totalAmount), count: num(x.count) }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Kpi label="Total Claims" value={num(s.totalCount).toLocaleString()} tone="blue" />
        <Kpi label="Total Amount" value={money(s.totalAmount)} tone="green" />
        <Kpi label="Avg / Claim" value={money(s.avgAmount)} tone="purple" />
        <Kpi label="Processing Rate" value={`${num(s.processingRate).toFixed(1)}%`} tone="teal" />
        <Kpi label="Avg Days" value={num(s.avgProcessingTimeDays).toFixed(1)} tone="orange" />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {statusData.length ? (
          <ChartCard title="Claims by Status" subtitle="Share of claims across the workflow">
            <PieChart>
              <Pie data={statusData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={100}
                innerRadius={55} paddingAngle={2} isAnimationActive={false}
                label={(e) => `${e.name}: ${e.count}`} labelLine={false}>
                {statusData.map((d) => <Cell key={d.status} fill={STATUS_COLORS[d.status] || '#94a3b8'} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n, p) => [v, statusLabel(p.payload.status)]} />
            </PieChart>
          </ChartCard>
        ) : <Empty />}

        {statusData.length ? (
          <ChartCard title="Amount by Status" subtitle="Dollar value per status">
            <BarChart data={statusData} margin={{ left: 6, right: 6 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="name" tick={axis} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis tick={axis} tickFormatter={moneyShort} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => money(v)} />
              <Bar dataKey="amount" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                {statusData.map((d) => <Cell key={d.status} fill={STATUS_COLORS[d.status] || '#94a3b8'} />)}
              </Bar>
            </BarChart>
          </ChartCard>
        ) : null}
      </div>

      {trend.length ? (
        <ChartCard title="Trend Over Time" subtitle="Claim volume (bars) and dollar amount (line)" height={320}>
          <ComposedChart data={trend} margin={{ left: 6, right: 6 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
            <XAxis dataKey="period" tick={axis} />
            <YAxis yAxisId="left" tick={axis} allowDecimals={false} />
            <YAxis yAxisId="right" orientation="right" tick={axis} tickFormatter={moneyShort} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => (n === 'Amount' ? money(v) : v)} />
            <Legend />
            <Bar yAxisId="left" dataKey="count" name="Claims" fill="#93c5fd" radius={[6, 6, 0, 0]} isAnimationActive={false} />
            <Area yAxisId="right" dataKey="amount" name="Amount" stroke="#2563eb" fill="#2563eb22" isAnimationActive={false} />
          </ComposedChart>
        </ChartCard>
      ) : null}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {custs.length ? (
          <ChartCard title="Top Customers" subtitle="By chargeback amount" height={340}>
            <BarChart data={custs} layout="vertical" margin={{ left: 20, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
              <XAxis type="number" tick={axis} tickFormatter={moneyShort} />
              <YAxis type="category" dataKey="name" tick={axis} width={130} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => money(v)} />
              <Bar dataKey="amount" fill="#16a34a" radius={[0, 6, 6, 0]} isAnimationActive={false} />
            </BarChart>
          </ChartCard>
        ) : null}
        {prods.length ? (
          <ChartCard title="Top Products" subtitle="By chargeback amount" height={340}>
            <BarChart data={prods} layout="vertical" margin={{ left: 20, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
              <XAxis type="number" tick={axis} tickFormatter={moneyShort} />
              <YAxis type="category" dataKey="name" tick={axis} width={130} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => money(v)} />
              <Bar dataKey="amount" fill="#7c3aed" radius={[0, 6, 6, 0]} isAnimationActive={false} />
            </BarChart>
          </ChartCard>
        ) : null}
      </div>
    </div>
  );
}

// ---- Financial Summary report -------------------------------------------
function FinancialCharts({ data }) {
  const s = data.summary || {};
  const statusData = (s.statusBreakdown || []).map((x) => ({ name: statusLabel(x.status), status: x.status, amount: num(x.amount), count: num(x.count) }));
  const trend = (data.monthlyRevenue || []).map((x) => ({ period: x.period, amount: num(x.totalAmount), count: num(x.count) }));
  const custs = (data.revenueByCustomer || []).slice(0, 10).map((x) => ({ name: x.customer?.name || 'Unknown', amount: num(x.totalAmount) }));
  const prods = (data.revenueByProduct || []).slice(0, 10).map((x) => ({ name: x.product?.name || 'Unknown', amount: num(x.totalAmount) }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Total Amount" value={money(s.totalAmount)} tone="green" />
        <Kpi label="Total Claims" value={num(s.totalCount).toLocaleString()} tone="blue" />
        <Kpi label="Customers" value={(data.revenueByCustomer || []).length} tone="purple" />
        <Kpi label="Products" value={(data.revenueByProduct || []).length} tone="orange" />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {statusData.length ? (
          <ChartCard title="Liability by Status" subtitle="Dollar share per status">
            <PieChart>
              <Pie data={statusData} dataKey="amount" nameKey="name" cx="50%" cy="50%" outerRadius={100}
                innerRadius={55} paddingAngle={2} isAnimationActive={false}
                label={(e) => statusLabel(e.status)} labelLine={false}>
                {statusData.map((d) => <Cell key={d.status} fill={STATUS_COLORS[d.status] || '#94a3b8'} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => money(v)} />
            </PieChart>
          </ChartCard>
        ) : <Empty />}

        {trend.length ? (
          <ChartCard title="Revenue Trend" subtitle="Chargeback dollars over time">
            <AreaChart data={trend} margin={{ left: 6, right: 6 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#16a34a" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#16a34a" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="period" tick={axis} />
              <YAxis tick={axis} tickFormatter={moneyShort} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => money(v)} />
              <Area dataKey="amount" name="Amount" stroke="#16a34a" fill="url(#rev)" isAnimationActive={false} />
            </AreaChart>
          </ChartCard>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {custs.length ? (
          <ChartCard title="Revenue by Customer" subtitle="Top 10" height={340}>
            <BarChart data={custs} layout="vertical" margin={{ left: 20, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
              <XAxis type="number" tick={axis} tickFormatter={moneyShort} />
              <YAxis type="category" dataKey="name" tick={axis} width={130} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => money(v)} />
              <Bar dataKey="amount" fill="#2563eb" radius={[0, 6, 6, 0]} isAnimationActive={false} />
            </BarChart>
          </ChartCard>
        ) : null}
        {prods.length ? (
          <ChartCard title="Revenue by Product" subtitle="Top 10" height={340}>
            <BarChart data={prods} layout="vertical" margin={{ left: 20, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
              <XAxis type="number" tick={axis} tickFormatter={moneyShort} />
              <YAxis type="category" dataKey="name" tick={axis} width={130} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => money(v)} />
              <Bar dataKey="amount" fill="#7c3aed" radius={[0, 6, 6, 0]} isAnimationActive={false} />
            </BarChart>
          </ChartCard>
        ) : null}
      </div>
    </div>
  );
}

// ---- Generic fallback (customers / products / contracts / edi) ----------
function GenericCharts({ data }) {
  if (!data || typeof data !== 'object') return <Empty />;

  // KPI cards from any numeric summary object.
  const summary = data.summary && typeof data.summary === 'object' ? data.summary : null;
  const kpis = summary
    ? Object.entries(summary).filter(([, v]) => typeof v === 'number' || (!isNaN(Number(v)) && v !== null && v !== ''))
    : [];

  // First array-of-objects property becomes a bar chart + table.
  const arrayKey = Object.keys(data).find((k) => Array.isArray(data[k]) && data[k].length && typeof data[k][0] === 'object');
  const rows = arrayKey ? data[arrayKey] : [];
  const sample = rows[0] || {};
  const labelKey = ['name', 'period', 'status', 'label', 'title'].find((k) => k in sample)
    || (sample.customer ? 'customer' : sample.product ? 'product' : Object.keys(sample)[0]);
  const valueKey = ['totalAmount', 'amount', 'count', 'total', 'value'].find((k) => typeof num(sample[k]) === 'number' && sample[k] != null && !isNaN(Number(sample[k])));
  const chartData = rows.slice(0, 12).map((r) => ({
    name: typeof r[labelKey] === 'object' ? (r[labelKey]?.name || 'Unknown') : String(r[labelKey] ?? ''),
    value: num(valueKey ? r[valueKey] : 0)
  }));
  const isMoney = valueKey === 'totalAmount' || valueKey === 'amount';

  return (
    <div className="space-y-5">
      {kpis.length ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {kpis.slice(0, 8).map(([k, v], i) => (
            <Kpi key={k} label={k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}
              value={/amount|revenue|value/i.test(k) ? money(v) : num(v).toLocaleString()}
              tone={['blue', 'green', 'purple', 'orange', 'teal', 'gray'][i % 6]} />
          ))}
        </div>
      ) : null}

      {chartData.length ? (
        <ChartCard title={arrayKey.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}
          subtitle={valueKey ? `By ${valueKey}` : ''} height={360}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 16 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
            <XAxis type="number" tick={axis} tickFormatter={isMoney ? moneyShort : undefined} />
            <YAxis type="category" dataKey="name" tick={axis} width={140} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => (isMoney ? money(v) : v)} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]} isAnimationActive={false}>
              {chartData.map((d, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Bar>
          </BarChart>
        </ChartCard>
      ) : (!kpis.length ? <Empty /> : null)}
    </div>
  );
}

// Aggregate a count/sum by a categorical key.
function tally(rows, keyFn, valFn) {
  const m = new Map();
  rows.forEach((r) => { const k = keyFn(r) || 'Unknown'; m.set(k, (m.get(k) || 0) + (valFn ? num(valFn(r)) : 1)); });
  return Array.from(m, ([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
}

const HBar = ({ title, subtitle, rows, isMoney = true, color = '#2563eb' }) => (
  <ChartCard title={title} subtitle={subtitle} height={340}>
    <BarChart data={rows} layout="vertical" margin={{ left: 20, right: 16 }}>
      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
      <XAxis type="number" tick={axis} tickFormatter={isMoney ? moneyShort : undefined} />
      <YAxis type="category" dataKey="name" tick={axis} width={140} />
      <Tooltip contentStyle={tooltipStyle} formatter={(v) => (isMoney ? money(v) : v)} />
      <Bar dataKey="value" radius={[0, 6, 6, 0]} isAnimationActive={false}>
        {rows.map((d, i) => <Cell key={i} fill={color === 'palette' ? PALETTE[i % PALETTE.length] : color} />)}
      </Bar>
    </BarChart>
  </ChartCard>
);

const CategoryPie = ({ title, subtitle, rows, isMoney = false }) => (
  <ChartCard title={title} subtitle={subtitle}>
    <PieChart>
      <Pie data={rows} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={55}
        paddingAngle={2} isAnimationActive={false} label={(e) => `${e.name}: ${isMoney ? moneyShort(e.value) : e.value}`} labelLine={false}>
        {rows.map((d, i) => <Cell key={i} fill={STATUS_COLORS[d.name?.toUpperCase?.()] || PALETTE[i % PALETTE.length]} />)}
      </Pie>
      <Tooltip contentStyle={tooltipStyle} formatter={(v) => (isMoney ? money(v) : v)} />
    </PieChart>
  </ChartCard>
);

// ---- Customer Analysis --------------------------------------------------
function CustomerCharts({ data }) {
  const s = data.summary || {};
  const rows = data.customers || [];
  const top = rows.map((r) => ({ name: r.customer?.name || 'Unknown', value: num(r.metrics?.totalAmount) }))
    .sort((a, b) => b.value - a.value).slice(0, 10);
  const byEntity = tally(rows, (r) => r.customer?.entityType || r.customer?.type);
  const byState = tally(rows, (r) => r.customer?.state || 'No state on file');
  const hasStates = byState.some((b) => b.name !== 'No state on file');
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Kpi label="Customers" value={num(s.totalCustomers).toLocaleString()} tone="blue" />
        <Kpi label="Chargebacks" value={num(s.totalChargebacks).toLocaleString()} tone="purple" />
        <Kpi label="Total Amount" value={money(s.totalAmount)} tone="green" />
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {top.length ? <HBar title="Top Customers" subtitle="By chargeback amount" rows={top} color="#16a34a" /> : <Empty />}
        {byEntity.length ? <CategoryPie title="Customers by Type" subtitle="Entity / class of trade" rows={byEntity} /> : null}
      </div>
      {hasStates ? (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <CategoryPie title="Customers by State" subtitle="Location breakdown" rows={byState} />
        </div>
      ) : null}
    </div>
  );
}

// ---- Product Performance ------------------------------------------------
function ProductCharts({ data }) {
  const s = data.summary || {};
  const rows = data.products || [];
  const top = rows.map((r) => ({ name: r.product?.name || 'Unknown', value: num(r.performance?.totalChargebackAmount) }))
    .sort((a, b) => b.value - a.value).slice(0, 10);
  const byClass = tally(rows, (r) => r.product?.therapeuticClass || 'Unclassified', (r) => r.performance?.totalChargebackAmount).slice(0, 7);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Kpi label="Products" value={num(s.totalProducts).toLocaleString()} tone="blue" />
        <Kpi label="Chargebacks" value={num(s.totalChargebackCount).toLocaleString()} tone="orange" />
        <Kpi label="Total Amount" value={money(s.totalChargebackAmount)} tone="green" />
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {top.length ? <HBar title="Top Products" subtitle="By chargeback amount" rows={top} color="#7c3aed" /> : <Empty />}
        {byClass.length ? <CategoryPie title="Amount by Therapeutic Class" subtitle="Top classes by dollars" rows={byClass} isMoney /> : null}
      </div>
    </div>
  );
}

// ---- Contract Performance (bare array) ----------------------------------
function ContractCharts({ data }) {
  const rows = Array.isArray(data) ? data : (data.contracts || []);
  const customers = rows.reduce((a, r) => a + num(r.metrics?.customerCount), 0);
  const prices = rows.reduce((a, r) => a + num(r.metrics?.priceCount), 0);
  const byStatus = tally(rows, (r) => r.contract?.status);
  const topPrices = rows.map((r) => ({ name: r.contract?.number || 'Unknown', value: num(r.metrics?.priceCount) }))
    .sort((a, b) => b.value - a.value).slice(0, 12);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Kpi label="Contracts" value={rows.length.toLocaleString()} tone="blue" />
        <Kpi label="Customer Links" value={customers.toLocaleString()} tone="teal" />
        <Kpi label="Price Rows" value={prices.toLocaleString()} tone="purple" />
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {byStatus.length ? <CategoryPie title="Contracts by Status" subtitle="Active / expired / etc." rows={byStatus} /> : <Empty />}
        {topPrices.length ? <HBar title="Price Rows per Contract" subtitle="Top 12 contracts" rows={topPrices} isMoney={false} color="palette" /> : null}
      </div>
    </div>
  );
}

// ---- Batch Processing ---------------------------------------------------
function BatchCharts({ data }) {
  const summary = data.summary || [];
  const batches = data.batches || [];
  const totalBatches = summary.reduce((a, r) => a + num(r._count?.status), 0);
  const totalRecords = summary.reduce((a, r) => a + num(r._sum?.recordCount), 0);
  const byStatus = summary.map((r) => ({ name: statusLabel(r.status), value: num(r._count?.status) }));
  const recRows = batches.map((r) => ({ name: r.batch?.batchNumber || 'Unknown', value: num(r.batch?.recordCount) }))
    .sort((a, b) => b.value - a.value).slice(0, 12);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Kpi label="Batches" value={totalBatches.toLocaleString()} tone="blue" />
        <Kpi label="Records" value={totalRecords.toLocaleString()} tone="teal" />
        <Kpi label="Statuses" value={summary.length} tone="gray" />
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {byStatus.length ? (
          <ChartCard title="Batches by Status" subtitle="Where batches sit in processing">
            <PieChart>
              <Pie data={byStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={55}
                paddingAngle={2} isAnimationActive={false} label={(e) => `${e.name}: ${e.value}`} labelLine={false}>
                {byStatus.map((d, i) => <Cell key={i} fill={STATUS_COLORS[String(d.name).toUpperCase().replace(' ', '_')] || PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ChartCard>
        ) : <Empty />}
        {recRows.length ? <HBar title="Records per Batch" subtitle="Most recent batches" rows={recRows} isMoney={false} color="palette" /> : null}
      </div>
    </div>
  );
}

/**
 * Heavy chart renderer for the Reports screen.
 * @param {{ type: string, data: object }} props  type = frontend tab id
 */
export default function ReportCharts({ type, data }) {
  // Unwrap the API response envelope { success, data } if it was passed through.
  if (data && typeof data === 'object' && !Array.isArray(data) && 'success' in data && data.data) data = data.data;
  if (!data) return <Empty label="No data available. Apply filters to generate a report." />;
  if (type === 'chargebacks') return <ChargebackCharts data={data} />;
  if (type === 'financial') return <FinancialCharts data={data} />;
  if (type === 'customers') return <CustomerCharts data={data} />;
  if (type === 'products') return <ProductCharts data={data} />;
  if (type === 'contracts') return <ContractCharts data={data} />;
  if (type === 'edi') return <BatchCharts data={data} />;
  return <GenericCharts data={data} />;
}
